import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { Modal } from 'atoms';
import {
  InvoiceKindPill,
  InvoiceStatusPill,
  MoneyCell,
  PopupContainer,
} from 'molecules';
import {
  IFakturowniaInvoiceListRow,
  useAllFakturowniaInvoices,
  useInvoiceMatching,
} from 'core/hooks';

import * as L from '../StudentDetail.styled';
import { formatMoneyCurrency } from './format';
import { IAgreement, IContribution } from './types';

interface IPickInvoiceForContributionPopupProps {
  contribution: IContribution;
  agreement: IAgreement;
  studentId: string;
  studentEmail: string | null;
  onClose: () => void;
  onLinked?: () => void;
}

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 2;

interface ISectionProps {
  title: string;
  count: number;
  emptyHint?: string;
  fetching: boolean;
  rows: IFakturowniaInvoiceListRow[];
  selectedId: string | null;
  contributionCurrency: string;
  onPick: (row: IFakturowniaInvoiceListRow) => void;
}

/**
 * One «source» section inside the popup (agreement / email / search).
 * The radio name is shared across sections so picking a row in one
 * section deselects whatever was picked in another — there's only one
 * invoice we're attaching to this contribution.
 */
const InvoiceSection = memo(
  ({
    title,
    count,
    emptyHint,
    fetching,
    rows,
    selectedId,
    contributionCurrency,
    onPick,
  }: ISectionProps) => {
    const { t } = useTranslation();
    if (count === 0 && !fetching) {
      return (
        <L.Subblock>
          <L.SubblockHeader>{title}</L.SubblockHeader>
          <L.Muted>{emptyHint || '—'}</L.Muted>
        </L.Subblock>
      );
    }
    return (
      <L.Subblock>
        <L.SubblockHeader>
          {title}
          <L.SubblockHeaderCount>· {count}</L.SubblockHeaderCount>
        </L.SubblockHeader>
        <L.SmallTable>
          <thead>
            <tr>
              <th aria-label="pick" style={{ width: 28 }} />
              <th>{t('pages.studentDetail.payments.linkPopup.col.number')}</th>
              <th>{t('pages.studentDetail.payments.linkPopup.col.date')}</th>
              <th>{t('pages.studentDetail.payments.linkPopup.col.status')}</th>
              <th>{t('pages.studentDetail.payments.linkPopup.col.email')}</th>
              <th style={{ textAlign: 'right' }}>
                {t('pages.studentDetail.payments.linkPopup.col.amount')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              // Spec 0004: an invoice already pinned to a взнос is
              // off-limits — the operator must unlink it there first.
              // Corrections/quotations aren't linkable at all.
              const disabled = Boolean(
                row.contribution || row.isCorrection || row.isQuotation,
              );
              const isSelected = selectedId === row.id;
              return (
                <tr
                  key={row.id}
                  style={{
                    opacity: disabled ? 0.55 : 1,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    background: isSelected ? '#eff6ff' : undefined,
                  }}
                  onClick={() => {
                    if (!disabled) onPick(row);
                  }}
                >
                  <td>
                    <input
                      type="radio"
                      name="link-invoice-pick"
                      checked={isSelected}
                      disabled={disabled}
                      onChange={() => {
                        if (!disabled) onPick(row);
                      }}
                      aria-label={row.number || ''}
                    />
                  </td>
                  <td style={{ fontWeight: 600 }}>
                    {row.number || `#${row.pk}`}
                    {row.currency && row.currency !== contributionCurrency ? (
                      <>
                        {' '}
                        <L.Tag tone="warn">{row.currency}</L.Tag>
                      </>
                    ) : null}
                    {disabled ? (
                      <>
                        {' '}
                        <L.Tag tone="neutral">
                          {t(
                            'pages.studentDetail.payments.linkPopup.alreadyLinked',
                          )}
                        </L.Tag>
                      </>
                    ) : null}
                  </td>
                  <td>
                    {row.issueDate
                      ? moment(row.issueDate).format('DD.MM.YYYY')
                      : '—'}
                  </td>
                  <td>
                    <InvoiceStatusPill status={row.status} />
                    {row.isCorrection || row.kind === 'correction' ? (
                      <>
                        {' '}
                        <InvoiceKindPill
                          kind={row.kind}
                          isCorrection={row.isCorrection}
                          isAdvance={row.isAdvance}
                          isFinal={row.isFinal}
                          isEstimate={row.isEstimate}
                          isQuotation={row.isQuotation}
                        />
                      </>
                    ) : null}
                  </td>
                  <td style={{ fontSize: 11, color: '#64748b' }}>
                    {row.buyerEmail || '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <MoneyCell
                      amount={row.priceGross}
                      currency={row.currency || ''}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </L.SmallTable>
      </L.Subblock>
    );
  },
);

/**
 * Inline popup that links an existing Fakturownia invoice to a scheduled
 * Contribution (взнос). Spec 0004: a single `linkInvoiceToContribution`
 * mutation pins the invoice — NO Payment is created here. The hidden
 * `students.Payment` is auto-provisioned by the backend once the linked
 * invoice is fully paid (or via the manual «оплачен вручную» mark).
 */
export const PickInvoiceForContributionPopup = memo(
  ({
    contribution,
    agreement,
    studentId,
    studentEmail,
    onClose,
    onLinked,
  }: IPickInvoiceForContributionPopupProps) => {
    const { t } = useTranslation();
    const { linkToContribution, loading } = useInvoiceMatching(studentId);

    const [selected, setSelected] = useState<IFakturowniaInvoiceListRow | null>(
      null,
    );
    const [rawSearch, setRawSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
      const id = window.setTimeout(() => {
        setDebouncedSearch(rawSearch.trim());
      }, SEARCH_DEBOUNCE_MS);
      return () => window.clearTimeout(id);
    }, [rawSearch]);

    const byAgreement = useAllFakturowniaInvoices({
      agreementId: agreement.id,
      direction: 'issued',
    });
    const byStudent = useAllFakturowniaInvoices({
      studentId,
      direction: 'issued',
    });
    const bySearch = useAllFakturowniaInvoices({
      search: debouncedSearch || undefined,
      direction: 'issued',
    });

    const agreementRows = byAgreement.data;
    const seenAfterAgreement = useMemo(() => {
      const s = new Set<string>();
      agreementRows.forEach((r) => s.add(r.id));
      return s;
    }, [agreementRows]);

    const emailRows = useMemo(
      () => byStudent.data.filter((r) => !seenAfterAgreement.has(r.id)),
      [byStudent.data, seenAfterAgreement],
    );
    const seenAfterEmail = useMemo(() => {
      const s = new Set<string>(seenAfterAgreement);
      emailRows.forEach((r) => s.add(r.id));
      return s;
    }, [seenAfterAgreement, emailRows]);

    const showSearch = debouncedSearch.length >= SEARCH_MIN_CHARS;
    const searchRows = useMemo(
      () =>
        showSearch
          ? bySearch.data.filter((r) => !seenAfterEmail.has(r.id))
          : [],
      [bySearch.data, seenAfterEmail, showSearch],
    );

    const contributionCurrency = agreement.currency || '';
    const remainderDue =
      contribution.debt ??
      contribution.remainder ??
      contribution.amountForeign ??
      0;

    const currencyMismatch = Boolean(
      selected?.currency && selected.currency !== contributionCurrency,
    );
    const amountMismatch = Boolean(
      selected &&
        typeof selected.priceGross === 'number' &&
        Math.abs((selected.priceGross || 0) - (remainderDue || 0)) > 0.01,
    );
    const canSubmit = Boolean(selected) && !loading;

    const onSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selected) return;
        // Single atomic link: invoice → взнос. Toasts (incl. translated
        // backend error codes) are handled inside the hook.
        const linked = await linkToContribution(selected.id, contribution.id);
        if (linked) {
          onLinked?.();
          onClose();
        }
      },
      [selected, linkToContribution, contribution.id, onLinked, onClose],
    );

    return (
      <Modal>
        <PopupContainer
          width="780px"
          title={t('pages.studentDetail.payments.linkPopup.title')}
          onClose={onClose}
        >
          <form onSubmit={onSubmit}>
            <L.FormBody>
              <div style={{ fontSize: 13, color: '#475569' }}>
                {t(
                  'pages.studentDetail.payments.linkPopup.contributionSummary',
                  {
                    date: moment(contribution.date).format('DD.MM.YYYY'),
                    amount: formatMoneyCurrency(
                      remainderDue,
                      contributionCurrency,
                    ),
                    agreement: agreement.agreementTitle,
                  },
                )}
              </div>

              <InvoiceSection
                title={t(
                  'pages.studentDetail.payments.linkPopup.sectionByAgreement',
                  { title: agreement.agreementTitle },
                )}
                count={agreementRows.length}
                emptyHint={t(
                  'pages.studentDetail.payments.linkPopup.noByAgreement',
                )}
                fetching={byAgreement.fetching}
                rows={agreementRows}
                selectedId={selected?.id ?? null}
                contributionCurrency={contributionCurrency}
                onPick={setSelected}
              />

              <InvoiceSection
                title={t(
                  'pages.studentDetail.payments.linkPopup.sectionByEmail',
                  { email: studentEmail || '—' },
                )}
                count={emailRows.length}
                emptyHint={t(
                  'pages.studentDetail.payments.linkPopup.noByEmail',
                )}
                fetching={byStudent.fetching}
                rows={emailRows}
                selectedId={selected?.id ?? null}
                contributionCurrency={contributionCurrency}
                onPick={setSelected}
              />

              <L.Subblock>
                <L.SubblockHeader>
                  {t('pages.studentDetail.payments.linkPopup.sectionSearch', {
                    count: searchRows.length,
                  })}
                </L.SubblockHeader>
                <L.FormInput
                  type="search"
                  placeholder={t(
                    'pages.studentDetail.payments.linkPopup.searchPlaceholder',
                  )}
                  value={rawSearch}
                  onChange={(e) => setRawSearch(e.target.value)}
                />
                {showSearch ? (
                  searchRows.length === 0 && !bySearch.fetching ? (
                    <L.Muted style={{ marginTop: 8 }}>
                      {t(
                        'pages.studentDetail.payments.linkPopup.noSearchResults',
                      )}
                    </L.Muted>
                  ) : (
                    <div style={{ marginTop: 8 }}>
                      <InvoiceSection
                        title=""
                        count={searchRows.length}
                        fetching={bySearch.fetching}
                        rows={searchRows}
                        selectedId={selected?.id ?? null}
                        contributionCurrency={contributionCurrency}
                        onPick={setSelected}
                      />
                    </div>
                  )
                ) : null}
              </L.Subblock>

              {currencyMismatch ? (
                <L.InlineNotice>
                  {t(
                    'pages.studentDetail.payments.linkPopup.currencyMismatch',
                    {
                      from: contributionCurrency,
                      to: selected?.currency,
                    },
                  )}
                </L.InlineNotice>
              ) : null}
              {amountMismatch ? (
                <L.InlineNotice>
                  {t('pages.studentDetail.payments.linkPopup.amountMismatch', {
                    paymentAmount: formatMoneyCurrency(
                      remainderDue,
                      contributionCurrency,
                    ),
                    invoiceAmount: formatMoneyCurrency(
                      selected?.priceGross,
                      selected?.currency,
                    ),
                  })}
                </L.InlineNotice>
              ) : null}

              <L.FormFooter>
                <L.PrimaryActionButton
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                >
                  {t('pages.studentDetail.payments.linkPopup.cancel')}
                </L.PrimaryActionButton>
                <L.PrimaryActionButton
                  type="submit"
                  tone="primary"
                  disabled={!canSubmit}
                >
                  {loading
                    ? t('pages.studentDetail.payments.linkPopup.submitting')
                    : t('pages.studentDetail.payments.linkPopup.submit')}
                </L.PrimaryActionButton>
              </L.FormFooter>
            </L.FormBody>
          </form>
        </PopupContainer>
      </Modal>
    );
  },
);
